import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useCan, useOrganizationId } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { type SnackbarKey, useSnackbar } from 'notistack';

import { useTemplateNameById } from '../../hooks';
import {
  useOrganizationAppointmentTypes,
  useUpdateOrganizationAppointmentType,
} from '../../store/hooks';
import type { AppointmentType } from '../../types';
import AppointmentTypeModal from '../AppointmentTypeModal';
import { ColorDot } from '../ColorDot';

import { buildLinkPlan, type LinkPlanItem, planItems } from './linkPlan';

export type TemplateTypeLinkingSource = 'template_editor' | 'ai_builder_completion';

export interface LinkAppointmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  /** The note template the picked types get linked to. */
  templateId: string;
  /** Display name of the template, used in copy and as the create-mode name seed. */
  templateName?: string;
  source: TemplateTypeLinkingSource;
}

/** Synthetic Autocomplete option for the inline "Create '<query>'" row. */
interface CreateTypeOption {
  __create: true;
}

type LinkOption = AppointmentType | CreateTypeOption;

const CREATE_OPTION: CreateTypeOption = { __create: true };

const isCreateOption = (option: LinkOption): option is CreateTypeOption =>
  '__create' in option;

type ModalPage = 'picker' | 'create';

/**
 * Search-or-create modal for linking appointment types to a note template
 * (GDN-2149). One entry point, both paths inside: a multi-select typeahead
 * over the org's appointment types, plus a "Create '<query>'" row that opens
 * the standard appointment-type create form pre-wired to this template.
 *
 * Additive/reassign-only: committing writes `template_id = <templateId>` on
 * each picked type. Picking a type that already uses a different template is
 * a reassignment and requires an explicit confirm step before committing.
 * The undo snackbar restores each committed type's previous template.
 */
export const LinkAppointmentTypeModal: React.FC<LinkAppointmentTypeModalProps> = ({
  open,
  onClose,
  templateId,
  templateName,
  source,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { template } = useEventTracking();
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId();

  const skip = !open || !canManageAppointmentTypes;
  const { appointmentTypes, isLoading } = useOrganizationAppointmentTypes(
    organizationId,
    undefined,
    { skip },
  );
  const templateNameById = useTemplateNameById({ skip });
  const { updateAppointmentType } = useUpdateOrganizationAppointmentType(organizationId);

  const [page, setPage] = useState<ModalPage>('picker');
  const [selected, setSelected] = useState<AppointmentType[]>([]);
  const [inputValue, setInputValue] = useState('');
  // Name to seed the create form with. Captured when the user chooses the
  // "Create …" row, BEFORE the Autocomplete clears its input on selection —
  // and held stable so AppointmentTypeModal's reset effect doesn't re-run.
  const [createName, setCreateName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);

  // Fresh picker state on every open.
  useEffect(() => {
    if (open) {
      setPage('picker');
      setSelected([]);
      setInputValue('');
      setCreateName('');
      setConfirmOpen(false);
    }
  }, [open]);

  // Stable reference so AppointmentTypeModal's form-reset effect (which lists
  // `defaultValues` in its deps) doesn't re-run every render and wipe the form.
  const createDefaultValues = useMemo(
    () => ({ overrides: { templateId } }),
    [templateId],
  );

  // Types already on this template are linked — only offer the rest.
  const options = useMemo<LinkOption[]>(
    () => [
      ...appointmentTypes.filter(type => type.template_id !== templateId),
      CREATE_OPTION,
    ],
    [appointmentTypes, templateId],
  );

  const plan = useMemo(() => buildLinkPlan(selected, templateId), [selected, templateId]);
  const commitCount = plan.additions.length + plan.reassignments.length;

  const currentTemplateLabel = useCallback(
    (type: AppointmentType) =>
      type.template_id
        ? templateNameById.get(type.template_id) ?? 'another template'
        : null,
    [templateNameById],
  );

  const handleClose = useCallback(() => {
    if (isCommitting) return;
    onClose();
  }, [isCommitting, onClose]);

  const commit = useCallback(async () => {
    // Invariant: this surface only ever writes a concrete template id.
    if (!templateId) return;
    const items = planItems(plan);
    if (!items.length) return;

    setIsCommitting(true);
    const committed: LinkPlanItem[] = [];
    let failedCount = 0;
    for (const item of items) {
      try {
        await updateAppointmentType(item.appointmentType.encounter_type_id, {
          template_id: templateId,
        });
        committed.push(item);
      } catch {
        failedCount += 1;
      }
    }
    setIsCommitting(false);
    setConfirmOpen(false);

    if (failedCount > 0) {
      enqueueSnackbar(
        `${failedCount} appointment type${failedCount === 1 ? '' : 's'} could not be linked. Please try again.`,
        { variant: 'error' },
      );
    }

    if (!committed.length) return;

    template.appointmentTypesChanged({
      template_id: templateId,
      action: 'link',
      linked_count: committed.filter(item => item.previousTemplateId === null).length,
      reassigned_count: committed.filter(item => item.previousTemplateId !== null).length,
      source,
    });

    const handleUndo = async (snackbarKey: SnackbarKey) => {
      closeSnackbar(snackbarKey);
      let undoFailed = 0;
      for (const item of committed) {
        try {
          // Restores the pre-commit state. This is the only path on this
          // surface that may write template_id = null — putting back the
          // previously-unlinked state of a fresh link.
          await updateAppointmentType(item.appointmentType.encounter_type_id, {
            template_id: item.previousTemplateId,
          });
        } catch {
          undoFailed += 1;
        }
      }
      template.appointmentTypesChanged({
        template_id: templateId,
        action: 'undo',
        linked_count: committed.filter(item => item.previousTemplateId === null).length,
        reassigned_count: committed.filter(item => item.previousTemplateId !== null).length,
        source,
      });
      if (undoFailed > 0) {
        enqueueSnackbar('Some changes could not be reverted. Please review your appointment types.', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Changes reverted', { variant: 'success' });
      }
    };

    enqueueSnackbar(
      `${committed.length} appointment type${committed.length === 1 ? '' : 's'} linked to this template`,
      {
        variant: 'success',
        autoHideDuration: 5000,
        action: snackbarKey => (
          <Button color="inherit" size="small" onClick={() => void handleUndo(snackbarKey)}>
            Undo
          </Button>
        ),
      },
    );

    onClose();
  }, [
    templateId,
    plan,
    updateAppointmentType,
    enqueueSnackbar,
    closeSnackbar,
    template,
    source,
    onClose,
  ]);

  const handleSubmit = useCallback(() => {
    if (plan.reassignments.length > 0) {
      setConfirmOpen(true);
      return;
    }
    void commit();
  }, [plan.reassignments.length, commit]);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, next: LinkOption[]) => {
      if (next.some(isCreateOption)) {
        const query = inputValue.trim();
        template.createAppointmentTypeClicked({
          template_id: templateId,
          source,
          has_query: query.length > 0,
        });
        // Capture the typed query now — the Autocomplete clears its input on
        // selection, so reading inputValue later would lose it.
        setCreateName(query || templateName || '');
        setSelected(next.filter((option): option is AppointmentType => !isCreateOption(option)));
        setPage('create');
        return;
      }
      setSelected(next as AppointmentType[]);
    },
    [template, templateId, source, inputValue, templateName],
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {page === 'create' ? (
          <AppointmentTypeModal
            open
            inline
            type="create"
            scope="organization"
            organizationId={organizationId}
            initialName={createName}
            defaultValues={createDefaultValues}
            existingAppointmentTypes={appointmentTypes}
            onBack={() => setPage('picker')}
            onDismiss={handleClose}
            onClose={() => setPage('picker')}
            onSave={onClose}
          />
        ) : (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>
                Link appointment types
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {templateName
                  ? `Appointments of a linked type automatically chart with "${templateName}".`
                  : 'Appointments of a linked type automatically chart with this template.'}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 1, overflow: 'visible' }}>
              <Autocomplete
                multiple
                openOnFocus
                disableCloseOnSelect
                loading={isLoading}
                options={options}
                value={selected}
                inputValue={inputValue}
                onInputChange={(_event, value) => setInputValue(value)}
                onChange={handleChange}
                getOptionLabel={option =>
                  isCreateOption(option) ? inputValue : option.encounter_type
                }
                isOptionEqualToValue={(option, value) => {
                  if (isCreateOption(option) || isCreateOption(value)) {
                    return isCreateOption(option) && isCreateOption(value);
                  }
                  return option.encounter_type_id === value.encounter_type_id;
                }}
                filterOptions={(candidates, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  const matches = candidates.filter(
                    (option): option is AppointmentType =>
                      !isCreateOption(option) &&
                      (!query || option.encounter_type.toLowerCase().includes(query)),
                  );
                  return [...matches, CREATE_OPTION];
                }}
                renderOption={(props, option) => {
                  if (isCreateOption(option)) {
                    return (
                      <Box
                        component="li"
                        {...props}
                        key="__create-appointment-type"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AddIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          {inputValue.trim()
                            ? `Create "${inputValue.trim()}"`
                            : 'Create a new appointment type'}
                        </Typography>
                      </Box>
                    );
                  }
                  const usesLabel = currentTemplateLabel(option);
                  return (
                    <Box
                      component="li"
                      {...props}
                      key={option.encounter_type_id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                    >
                      <ColorDot colorEnum={option.color} size={14} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {option.encounter_type}
                        </Typography>
                        {usesLabel && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'warning.main', display: 'block' }}
                          >
                            {`Currently uses "${usesLabel}" — linking moves it to this template`}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                renderValue={(value, getTagProps) =>
                  value.map((option, index) => {
                    if (isCreateOption(option)) return null;
                    const { key: _key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={option.encounter_type_id}
                        size="small"
                        icon={<ColorDot colorEnum={option.color} size={12} />}
                        label={option.encounter_type}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder={selected.length ? undefined : 'Search appointment types…'}
                  />
                )}
                slotProps={{
                  popper: { placement: 'bottom-start' },
                  listbox: { sx: { maxHeight: 320 } },
                }}
              />
              {plan.reassignments.length > 0 && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                  {`${plan.reassignments.length} of the selected type${plan.reassignments.length === 1 ? '' : 's'} already use${plan.reassignments.length === 1 ? 's' : ''} another template — you'll be asked to confirm the move.`}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleClose} disabled={isCommitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={commitCount === 0 || isCommitting}
                startIcon={isCommitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {commitCount > 1 ? `Link ${commitCount} types` : 'Link type'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        variant="warning"
        title="Move appointment types to this template?"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void commit()}
        confirmText="Move and link"
        cancelText="Cancel"
        loading={isCommitting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {plan.reassignments.map(item => (
            <Box
              key={item.appointmentType.encounter_type_id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ColorDot colorEnum={item.appointmentType.color} size={12} />
              <Typography variant="body2">
                {`"${item.appointmentType.encounter_type}" currently uses "${currentTemplateLabel(item.appointmentType) ?? 'another template'}"`}
              </Typography>
            </Box>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Notes for these appointment types will use this template going forward.
          </Typography>
        </Box>
      </ConfirmDialog>
    </>
  );
};
